import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import SucursalVentaSearch from '../SucursalVentaSearch';
import { SucursalVenta } from '../../services/sucursalesVentaService';
import type { SucursalUsuarioDraft } from '../../utils/syncSucursalesUsuario';

interface SucursalesUsuarioSectionProps {
  value: SucursalUsuarioDraft[];
  onChange: (items: SucursalUsuarioDraft[]) => void;
  disabled?: boolean;
}

const SucursalesUsuarioSection: React.FC<SucursalesUsuarioSectionProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const excludeNroSuc = useMemo(() => value.map((s) => s.nroSuc), [value]);

  const handleAgregar = (sucursal: SucursalVenta) => {
    if (value.some((s) => s.nroSuc === sucursal.nroSuc)) return;
    const nueva: SucursalUsuarioDraft = {
      nroSuc: sucursal.nroSuc,
      nombreSucursal: sucursal.nombreSucursal?.trim() || `Sucursal ${sucursal.nroSuc}`,
      preferida: value.length === 0,
    };
    onChange([...value, nueva]);
  };

  const handleEliminar = (nroSuc: number) => {
    const restantes = value.filter((s) => s.nroSuc !== nroSuc);
    if (restantes.length === 0) {
      onChange([]);
      return;
    }
    const eliminadaEraPreferida = value.find((s) => s.nroSuc === nroSuc)?.preferida;
    if (eliminadaEraPreferida && !restantes.some((s) => s.preferida)) {
      onChange(restantes.map((s, idx) => ({ ...s, preferida: idx === 0 })));
      return;
    }
    onChange(restantes);
  };

  const handleMarcarPreferida = (nroSuc: number) => {
    onChange(value.map((s) => ({ ...s, preferida: s.nroSuc === nroSuc })));
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Sucursales de venta asignadas
        </Typography>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          disabled={disabled}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            py: 1,
            fontSize: '0.95rem',
          }}
        >
          Agregar sucursal
        </Button>
      </Stack>

      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Sin sucursales asignadas. El usuario no podrá operar en ventas hasta que se asigne al menos
          una.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Sucursal</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }} align="center">
                  Preferida
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 80 }} align="center">
                  Acción
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {value.map((item) => (
                <TableRow key={item.nroSuc}>
                  <TableCell>
                    {item.nombreSucursal} ({item.nroSuc})
                    {item.preferida && (
                      <Chip label="Preferida" size="small" color="primary" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={item.preferida ? 'Sucursal preferida' : 'Marcar como preferida'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMarcarPreferida(item.nroSuc)}
                          disabled={disabled || item.preferida}
                          sx={
                            item.preferida
                              ? {
                                  color: '#f9a825',
                                  '&.Mui-disabled': { color: '#f9a825' },
                                  '& .MuiSvgIcon-root': { color: '#f9a825' },
                                }
                              : undefined
                          }
                        >
                          {item.preferida ? (
                            <StarIcon fontSize="small" />
                          ) : (
                            <StarBorderIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Quitar asignación">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleEliminar(item.nroSuc)}
                          disabled={disabled}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SucursalVentaSearch
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSelect={handleAgregar}
        excludeNroSuc={excludeNroSuc}
      />
    </Box>
  );
};

export default SucursalesUsuarioSection;
